/**
 * USP badge layout for product cards — expandable single-line row with "+N" toggle.
 *
 * Measures the badge list of every `.product-card__usp-wrap--expandable`, hides the
 * badges that overflow one line and shows a "+N" toggle instead (click = expand).
 * The measuring logic is unchanged from the previous inline implementation
 * (layout/theme.liquid); this module replaces HOW re-measuring is triggered:
 *
 *   before: one MutationObserver on document.body (subtree) firing on EVERY DOM
 *           mutation of the page (countdown ticks, drawers, …) + blind
 *           setTimeout retries at 500/1500/3000 ms  → layout thrashing, INP risk.
 *   now:    narrow childList observers (NO subtree) exactly where new cards can
 *           appear, plus the theme's own events:
 *           - <product-recommendations>: fetches HTML and injects via
 *             replaceChildren (theme.js ProductRecommendations) → childList fires once.
 *           - .product-list: Load-more / infinite scroll appends cards here.
 *           - the .shopify-section wrapper around a .product-list: facet updates
 *             replace the wrapper's children wholesale (theme.js facet:update
 *             handler uses shopifySection.replaceChildren) → childList fires once,
 *             after which the new inner containers are (re-)observed.
 *           - document events cart:change / cart:refresh (drawer re-renders) and
 *             shopify:section:load (theme editor).
 *
 * Text/attribute mutations deep inside those wrappers (e.g. countdown ticks) do
 * NOT fire childList-only observers on the wrapper, so idle pages stay idle.
 *
 * Loaded conditionally from layout/theme.liquid when
 * settings.show_product_card_usp_badges is on and product_card_usp_layout != 'stack'.
 */

(function () {
  var SEL_WRAP = '.product-card__usp-wrap--expandable';

  var ro = ('ResizeObserver' in window)
    ? new ResizeObserver(function (entries) {
        entries.forEach(function (e) { measure(e.target); });
      })
    : null;

  function measure(wrap) {
    if (!wrap || !wrap.isConnected) return;
    if (wrap.classList.contains('is-expanded')) return;
    var list = wrap.querySelector('.product-card__usp-badges');
    if (!list) return;
    var badges = list.querySelectorAll('.product-card__usp-badge');
    var toggle = list.querySelector('.product-card__usp-toggle');
    if (!toggle || badges.length === 0) return;

    /* Reset: show all + hide toggle so we can measure natural sizes */
    badges.forEach(function (b) { b.classList.remove('product-card__usp-badge--collapsed'); });
    toggle.hidden = true;

    var maxWidth = list.clientWidth;
    if (maxWidth <= 1) {
      /* List has no width yet, retry after next paint */
      requestAnimationFrame(function () { measure(wrap); });
      return;
    }

    var gap = parseFloat(getComputedStyle(list).columnGap || getComputedStyle(list).gap) || 0;

    /* Pass 1: do all badges fit in one row without toggle? */
    var totalWidth = 0;
    badges.forEach(function (b, i) {
      totalWidth += b.offsetWidth + (i > 0 ? gap : 0);
    });
    if (totalWidth <= maxWidth + 1) return; /* all fit */

    /* Pass 2: hide overflowing badges, reserve room for toggle */
    toggle.hidden = false;
    var toggleW = toggle.offsetWidth + gap;
    toggle.hidden = true;
    if (toggleW <= 0) toggleW = 56;

    var allowed = maxWidth - toggleW;
    var hiddenCount = 0;
    var runningWidth = 0;
    var anyShown = false;

    badges.forEach(function (b, i) {
      var w = b.offsetWidth + (anyShown ? gap : 0);
      /* Always show first badge if it fits at all (even if it exceeds allowed budget by a bit) */
      if (i === 0 || runningWidth + w <= allowed) {
        runningWidth += w;
        anyShown = true;
      } else {
        b.classList.add('product-card__usp-badge--collapsed');
        hiddenCount++;
      }
    });

    if (hiddenCount > 0) {
      var label = toggle.querySelector('.product-card__usp-toggle-label');
      if (label) label.textContent = '+' + hiddenCount;
      toggle.hidden = false;
    }
  }

  function expand(wrap) {
    var list = wrap.querySelector('.product-card__usp-badges');
    if (!list) return;
    wrap.classList.add('is-expanded');
    list.querySelectorAll('.product-card__usp-badge--collapsed').forEach(function (b) {
      b.classList.remove('product-card__usp-badge--collapsed');
    });
    var toggle = list.querySelector('.product-card__usp-toggle');
    if (toggle) toggle.hidden = true;
  }

  function setup(wrap) {
    if (wrap.dataset.uspInit) {
      measure(wrap);
      return;
    }
    wrap.dataset.uspInit = '1';
    var toggle = wrap.querySelector('.product-card__usp-toggle');
    if (toggle) {
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        expand(wrap);
      });
    }
    if (ro) ro.observe(wrap);
    measure(wrap);
  }

  /* ---- discovery: narrow, childList-only observers where cards can appear ---- */

  var observed = ('WeakSet' in window) ? new WeakSet() : null;
  var scanTimer;

  var containerObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length > 0) {
        scheduleScan();
        return;
      }
    }
  });

  function observeContainer(el) {
    if (!el) return;
    if (observed) {
      if (observed.has(el)) return;
      observed.add(el);
    } else if (el.dataset && el.dataset.uspObserved) {
      return;
    } else if (el.dataset) {
      el.dataset.uspObserved = '1';
    }
    containerObserver.observe(el, { childList: true });
  }

  function discoverContainers() {
    /* async product recommendations (replaceChildren after fetch) */
    document.querySelectorAll('product-recommendations').forEach(observeContainer);
    /* load-more / infinite scroll appends into the grid */
    document.querySelectorAll('.product-list').forEach(function (list) {
      observeContainer(list);
      /* facet updates replace ALL children of the section wrapper — observe it
         so we re-discover the freshly injected grid afterwards */
      observeContainer(list.closest('.shopify-section'));
    });
  }

  function scan() {
    document.querySelectorAll(SEL_WRAP).forEach(setup);
    discoverContainers();
  }

  function scheduleScan() {
    clearTimeout(scanTimer);
    scanTimer = setTimeout(scan, 80);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  /* Drawer / cart surfaces re-render on these theme events */
  document.addEventListener('cart:change', scheduleScan);
  document.addEventListener('cart:refresh', scheduleScan);

  /* Theme editor: sections get re-rendered on the fly */
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', scheduleScan);
  }

  /* Fallback for browsers without ResizeObserver */
  if (!ro) {
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        document.querySelectorAll(SEL_WRAP).forEach(measure);
      }, 100);
    });
  }
})();
